import {
  MOTOR_COMP_INCLUDED_BENEFITS,
  MOTOR_COMP_OTHER_BENEFITS,
  MOTOR_COVER_LABELS,
  MOTOR_TPO_BENEFITS,
} from '@/constants/motorQuoteBenefits';
import { MOTOR_COVER_COMPREHENSIVE, MOTOR_COVER_TPO } from '@/constants/motorPrivate';
import {
  createPdfLayout,
  formatPdfCurrency,
  formatPdfDate,
  renderBenefitsTable,
  renderCompanyHeader,
  renderDocumentTitle,
  renderGrandTotal,
  renderInfoBox,
  renderIntermediaryStrip,
  renderInvoiceMeta,
  renderTableHeader,
  renderTableRow,
  renderValidityAndFooter,
} from '@/utils/quotePdfTheme';

function resolvePremiums(quote) {
  const prem = quote.riskPremDto || {};
  const total = Number(prem.totalPremium || quote.premium || 0);
  let basic = Number(prem.basicPremium || quote.basicPremium || 0);

  // Quote API often returns totalPremium only — derive net premium like agent live calc.
  if (!basic && total) {
    basic = Math.round((total - 40) / 1.0045);
  }

  const trainingLevy = prem.trainingLevy != null
    ? Number(prem.trainingLevy)
    : Math.round(basic * 0.002);
  const phf = prem.phf != null ? Number(prem.phf) : Math.ceil(basic * 0.0025);
  const stampDuty = prem.stampDuty != null ? Number(prem.stampDuty) : 40;

  return {
    basic,
    trainingLevy,
    phf,
    stampDuty,
    total: total || basic + trainingLevy + phf + stampDuty,
  };
}

function resolveCoverLabel(quote) {
  if (quote.coverLabel) return quote.coverLabel;
  const id = Number(quote.covertypeId);
  return MOTOR_COVER_LABELS[id] || 'Motor Insurance';
}

function resolveVehicleLine(quote) {
  if (quote.vehicleLine) return quote.vehicleLine;
  const parts = [quote.make, quote.model, quote.year].filter(Boolean);
  return parts.join(' ') || '—';
}

function formatBenefitLimit(b) {
  if (b.insuredAmount) return `Ksh ${formatPdfCurrency(b.insuredAmount)}`;
  if (b.premium) return `+ Ksh ${formatPdfCurrency(b.premium)}`;
  return b.limit || 'Included';
}

function mapApiBenefitsToRows(items) {
  return (items || []).map((b) => ({
    label: b.sectionName || b.benefit || b.sectDesc || 'Benefit',
    limit: formatBenefitLimit(b),
  }));
}

function resolveBenefits(quote) {
  const id = Number(quote.covertypeId);
  if (Array.isArray(quote.benefitDetails) && quote.benefitDetails.length) {
    const selected = mapApiBenefitsToRows(quote.benefitDetails);
    if (id === MOTOR_COVER_COMPREHENSIVE) {
      return [...MOTOR_COMP_INCLUDED_BENEFITS, ...selected];
    }
    return selected;
  }
  if (Array.isArray(quote.additionalBenefits) && quote.additionalBenefits.length) {
    const selected = mapApiBenefitsToRows(quote.additionalBenefits);
    if (id === MOTOR_COVER_COMPREHENSIVE) {
      return [...MOTOR_COMP_INCLUDED_BENEFITS, ...selected];
    }
    return selected;
  }
  if (id === MOTOR_COVER_TPO) return MOTOR_TPO_BENEFITS;
  if (id === MOTOR_COVER_COMPREHENSIVE) {
    return [...MOTOR_COMP_INCLUDED_BENEFITS, ...MOTOR_COMP_OTHER_BENEFITS];
  }
  return MOTOR_COMP_OTHER_BENEFITS;
}

/**
 * Normalize motor quote API data for PDF/email export.
 */
export function buildMotorQuoteExportPayload({
  quote = {},
  activeRisk = null,
  coverLabel = '',
  vehicleLine = '',
  premium = 0,
  rateApplied = null,
  clientName = '',
  clientEmail = '',
} = {}) {
  const risk = activeRisk || quote?.covertypes?.[0] || {};
  const car = risk.car || {};
  const covertypeId = Number(risk.covertypeId || quote.covertypeId || 0);
  const prem = risk.riskPremDto || quote.riskPremDto || {};
  return {
    quoteId: quote.quoteId || risk.quoteId,
    covertypeId,
    coverLabel: coverLabel || resolveCoverLabel({ covertypeId }),
    vehicleLine: vehicleLine || [car.make, car.model, car.yearOfManufacture].filter(Boolean).join(' · '),
    make: car.make || quote.make || '',
    model: car.model || quote.model || '',
    year: car.yearOfManufacture || quote.year || '',
    valueOfCar: car.valueOfCar ?? car.value ?? quote.valueOfCar ?? 0,
    rateApplied: rateApplied ?? risk.rateApplied ?? quote.rateApplied,
    premium: Number(premium || prem.totalPremium || 0),
    riskPremDto: prem,
    clientName,
    clientEmail: clientEmail || quote.email || '',
    coverFrom: quote.coverFrom || '',
    additionalBenefits: risk.additionalBenefits || quote.additionalBenefits || [],
    benefitDetails: quote.benefitDetails || [],
  };
}

export function buildMotorQuotePDF(doc, quote, options = {}) {
  const clientName = options.clientName || quote.clientName || quote.fullName || 'N/A';
  const clientEmail = options.clientEmail || quote.clientEmail || quote.email || 'N/A';
  const agentName = options.agentName || 'Direct (Customer Portal)';
  const quoteId = quote.quoteId || '—';
  const invoiceNo = `INV-${String(quoteId).replace(/\D/g, '').padStart(6, '0').slice(-6) || quoteId}`;
  const coverLabel = resolveCoverLabel(quote);
  const vehicleLine = resolveVehicleLine(quote);
  const {
    basic, trainingLevy, phf, stampDuty, total,
  } = resolvePremiums(quote);
  const isTpo = Number(quote.covertypeId) === MOTOR_COVER_TPO;

  const layout = createPdfLayout(doc);
  let y = renderCompanyHeader(doc, layout, { agentName });
  y = renderDocumentTitle(doc, layout, 'MOTOR INSURANCE INVOICE', y);
  y = renderInvoiceMeta(doc, layout, { invoiceNo, clientName, clientEmail }, y);

  const boxH = 30;
  const colW = (layout.contentW - 6) / 2;
  const leftRows = [
    ['Name', clientName],
    ['Email', clientEmail],
    ['Quote ID', String(quoteId)],
  ];
  const rightRows = [
    ['Vehicle', vehicleLine],
    ['Cover', coverLabel],
    ...(quote.year ? [['Year', String(quote.year)]] : []),
    ...(!isTpo && quote.valueOfCar
      ? [['Sum insured', `Ksh ${formatPdfCurrency(quote.valueOfCar)}`]]
      : []),
    ...(quote.rateApplied != null && !isTpo
      ? [['Rate', `${quote.rateApplied}% of sum insured`]]
      : []),
  ];
  renderInfoBox(doc, layout.margin, y, colW, boxH, 'CLIENT DETAILS', leftRows);
  renderInfoBox(doc, layout.margin + colW + 6, y, colW, boxH, 'VEHICLE & COVER', rightRows);
  y += boxH + 6;

  y = renderIntermediaryStrip(doc, layout, agentName, y);
  y = renderTableHeader(doc, layout, 'INVOICE SUMMARY', 'Amount (Ksh)', y);

  const premiumLabel = isTpo
    ? 'TPO Flat Rate — Motor Insurance'
    : 'Motor Insurance Premium';
  y = renderTableRow(doc, layout, premiumLabel, basic, y);
  y = renderTableRow(doc, layout, 'Training Levy (0.2%)', trainingLevy, y, { dim: true });
  y = renderTableRow(doc, layout, 'PHF (0.25%)', phf, y, { dim: true });
  y = renderTableRow(doc, layout, 'Stamp Duty', stampDuty, y, { dim: true });
  y = renderGrandTotal(doc, layout, total, y);

  const benefits = resolveBenefits(quote).map((b) => ({
    label: b.label,
    limit: b.limit,
  }));
  y = renderBenefitsTable(doc, layout, benefits, y, { limitHeader: 'Limit / details' });

  if (isTpo) {
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(253, 230, 138);
    doc.setLineWidth(0.3);
    doc.roundedRect(layout.margin, y, layout.contentW, 8, 2, 2, 'FD');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(146, 64, 14);
    doc.text(
      'TPO is charged at a flat statutory rate — no vehicle value required.',
      layout.margin + 3,
      y + 5.5,
    );
    y += 14;
  }

  renderValidityAndFooter(doc, layout, y);
}

export function buildMotorQuoteEmailHtml(quote, customerName, options = {}) {
  const name = customerName || quote.clientName || 'Customer';
  const agentName = options.agentName || 'Direct (Customer Portal)';
  const quoteId = quote.quoteId || '—';
  const invoiceNo = `INV-${String(quoteId).replace(/\D/g, '').padStart(6, '0').slice(-6) || quoteId}`;
  const coverLabel = resolveCoverLabel(quote);
  const vehicleLine = resolveVehicleLine(quote);
  const { total } = resolvePremiums(quote);
  const dateStr = formatPdfDate();
  const benefits = resolveBenefits(quote);

  const benefitRows = benefits
    .slice(0, 8)
    .map((b, i) => {
      const bg = i % 2 === 0 ? 'background:#FAFCFF;' : '';
      return `<tr style="border-bottom:1px solid #f1f5f9;${bg}">
        <td style="padding:6px 10px;font-size:11px;font-weight:600;color:#1E4276;">${b.label}</td>
        <td style="padding:6px 10px;font-size:11px;text-align:right;color:#64748b;">${b.limit}</td>
      </tr>`;
    })
    .join('');

  return `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#f1f5f9;font-family:Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f1f5f9;padding:24px 0;"><tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(30,66,118,0.08);">
<tr><td style="padding:16px 20px;border-bottom:1px solid #e2e8f0;">
  <table width="100%"><tr>
    <td><div style="font-size:16px;font-weight:800;color:#1E4276;">Pacis Insurance</div>
    <div style="font-size:11px;color:#64748b;margin-top:2px;">With you every step of the way</div></td>
    <td style="text-align:right;font-size:9px;color:#64748b;line-height:1.5;">
      <div style="font-weight:700;color:#1E4276;">Pacis Insurance Company Limited</div>
      Pacis Centre, 4th Floor, off Waiyaki Way<br>P.O BOX 1870-00200, Nairobi<br>
      <span style="color:#00AEEF;font-weight:700;">INTERMEDIARY: ${agentName}</span>
    </td>
  </tr></table>
</td></tr>
<tr><td style="background:#1E4276;padding:10px 20px;">
  <table width="100%"><tr>
    <td style="font-size:12px;font-weight:800;color:#fff;letter-spacing:0.4px;">MOTOR INSURANCE INVOICE</td>
    <td style="text-align:right;font-size:9px;color:rgba(255,255,255,0.75);">Date: ${dateStr}</td>
  </tr></table>
</td></tr>
<tr><td style="padding:16px 20px;">
  <p style="margin:0 0 10px;color:#1E4276;font-size:14px;">Dear ${name},</p>
  <p style="margin:0 0 14px;color:#475569;font-size:13px;line-height:1.5;">Please find your motor insurance quotation below.</p>
  <table width="100%" style="margin-bottom:12px;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
    <tr style="background:#EBF5FF;"><td colspan="2" style="padding:6px 10px;font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;">Quote summary</td></tr>
    <tr><td style="padding:6px 10px;font-size:10px;color:#64748b;width:35%;">Invoice No.</td><td style="padding:6px 10px;font-size:11px;font-weight:700;color:#1E4276;">${invoiceNo}</td></tr>
    <tr><td style="padding:6px 10px;font-size:10px;color:#64748b;">Vehicle</td><td style="padding:6px 10px;font-size:11px;font-weight:700;color:#1E4276;">${vehicleLine}</td></tr>
    <tr><td style="padding:6px 10px;font-size:10px;color:#64748b;">Cover</td><td style="padding:6px 10px;font-size:11px;font-weight:700;color:#1E4276;">${coverLabel}</td></tr>
    <tr><td style="padding:6px 10px;font-size:10px;color:#64748b;">Quote ref</td><td style="padding:6px 10px;font-size:11px;font-weight:700;color:#1E4276;">${quoteId}</td></tr>
  </table>
  <div style="font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;margin-bottom:6px;border-bottom:2px solid #1E4276;padding-bottom:4px;">Benefits included</div>
  <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;margin-bottom:14px;">${benefitRows}</table>
  <p style="margin:0;font-size:20px;font-weight:800;color:#184679;">Total premium: KES ${formatPdfCurrency(total)}</p>
  <p style="margin:12px 0 0;font-size:11px;color:#94A3B8;">Valid 30 days. Cover commences upon receipt of premium.</p>
</td></tr>
<tr><td style="padding:12px 20px;background:#FFFBEB;border-top:1px solid #FDE68A;font-size:10px;color:#92400e;">
  This is a computer-generated quotation and does not require a signature.
</td></tr>
</table></td></tr></table></body></html>`;
}

export async function openMotorQuotePdf(quote, clientName, options = {}) {
  if (!quote?.quoteId) return false;
  if (!window.jspdf?.jsPDF) {
    throw new Error('PDF library not loaded');
  }
  const { jsPDF: JsPDF } = window.jspdf;
  const doc = new JsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  buildMotorQuotePDF(doc, quote, {
    ...options,
    clientName: clientName || quote.clientName,
  });
  const blob = doc.output('bloburl');
  window.open(blob, '_blank');
  return true;
}
